import React, { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { all_routes } from "../router/all_routes"
import Table from "../../core/common/dataTable/index"
import {
  getAllRoles,
  deleteRole,
  type Role,
  type RoleFilters,
  Permission,
} from "../../service/roles-permission"
import { toast } from "react-toastify"
import { useSelector } from "react-redux"
import { selectUser } from "../../core/data/redux/slice/user"

const RolesPermissions = () => {
  const user = useSelector(selectUser)
  const routes = all_routes
  const navigate = useNavigate()
  const [roles, setRoles] = useState<Role[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10,
  })
  const [filters, setFilters] = useState<RoleFilters>({
    page: 1,
    limit: 10,
    sortBy: "createdAt",
    order: "desc",
  })

  const [deleteRoleData, setDeleteRoleData] = useState<{
    id: string
    name: string
  } | null>(null)

  // Delete stays blocked for every core system role -- removing "teacher"
  // here would orphan the role every real teacher account points at.
  const protectedRoles = ["owner", "teacher", "admin", "student", "parent"]
  // Edit is now allowed for Teacher specifically (teachers log in and the
  // school wants to configure what they can access), while the other 4
  // structural roles stay edit-locked too. NOTE: ticking modules here does
  // NOT control most teacher behaviour -- marks entry/grading etc. are
  // gated by TeacherAssignment (which classes a teacher is assigned to),
  // not this permission grid. This only affects features that actually
  // check the permission grid for role=teacher.
  const editProtectedRoles = ["owner", "admin", "student", "parent"]

  const isRoleProtected = (roleName: string): boolean => {
    return protectedRoles.includes(roleName.toLowerCase())
  }

  const isRoleEditProtected = (roleName: string): boolean => {
    return editProtectedRoles.includes(roleName.toLowerCase())
  }

  const fetchRoles = async () => {
    setLoading(true)
    try {
      const response = await getAllRoles(filters)
      setRoles(response.data)
      setPagination(response.pagination)
    } catch (error) {
      console.error("Error fetching roles:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRoles()
  }, [filters])

  const handleDeleteRole = async (roleId: string) => {
    if (!deleteRoleData) return

    if (isRoleProtected(deleteRoleData.name)) {
      toast.error("This role is protected and cannot be deleted.")
      return
    }

    setLoading(true)
    try {
      await deleteRole(roleId)
      const el = document.getElementById("delete-modal")
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal")
        el.click()
        el.removeAttribute("data-bs-dismiss")
      }
      setDeleteRoleData(null)
      toast.success("Role deleted successfully!")
      fetchRoles()
    } catch (error: any) {
      console.error("Error deleting role:", error)
      toast.error(
        error.response?.data?.message ||
        "Error deleting role. Please try again."
      )
    } finally {
      setLoading(false)
    }
  }

  const handlePageChange = (page: number, pageSize: number) => {
    setFilters(prev => ({
      ...prev,
      page,
      limit: pageSize,
    }))
  }

  const handleAddRoleClick = () => {
    navigate(routes.permissions)
  }

  const handleEditRoleClick = (roleId: string) => {
    navigate(`${routes.permissions}/${roleId}`)
  }

  const openDeleteModal = (roleId: string, roleName: string) => {
    setDeleteRoleData({ id: roleId, name: roleName })
  }

  const columns = [
    {
      title: "Role Name",
      dataIndex: "name",
      render: (name: string) => {
        return name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : ""
      },
      sorter: (a: Role, b: Role) => a.name.localeCompare(b.name),
    },
    {
      title: "Modules",
      dataIndex: "permissions",
      render: (permissions: Permission[], record: Role) => {
        const roleName = record.name?.toLowerCase()

        if (roleName === "owner" || roleName === "admin") {
          return <span className="text-success">All Permissions</span>
        }

        if (!permissions || permissions.length === 0) {
          return <span className="text-muted">No Modules</span>
        }

        return (
          <span>
            {permissions.length > 1
              ? `${permissions.length} modules`
              : `${permissions.length} module`}
          </span>
        )
      },
    },

    {
      title: "Created On",
      dataIndex: "createdAt",
      sorter: (a: Role, b: Role) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (createdAt: string) => new Date(createdAt).toLocaleDateString(),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (isActive: boolean) => (
        <span
          className={`badge ${isActive ? "badge badge-soft-success" : "badge badge-soft-danger"
            }`}
        >
          {isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: Role) => (
        <div className="d-flex align-items-center">
          <button
            className={`btn btn-outline-light bg-white btn-icon d-flex align-items-center justify-content-center rounded-circle p-0 me-2 ${isRoleEditProtected(record.name)
                ? "disabled"
                : user.permissions?.includes("role.edit") ||
                  !user.permissions?.length
                  ? ""
                  : "not-allowed"
              }`}
            onClick={() => {
              if (!isRoleEditProtected(record.name)) {
                handleEditRoleClick(record._id)
              }
            }}
            disabled={isRoleEditProtected(record.name)}
            title={
              isRoleEditProtected(record.name)
                ? "This role is protected and cannot be edited"
                : "Edit role"
            }
          >
            <i
              className={`ti ti-edit-circle ${isRoleEditProtected(record.name) ? "text-muted" : "text-primary"
                }`}
            />
          </button>
          <button
            className={`btn btn-outline-light bg-white btn-icon d-flex align-items-center justify-content-center rounded-circle p-0 me-3 ${isRoleProtected(record.name)
                ? "disabled"
                : user.permissions?.includes("role.delete") ||
                  !user.permissions?.length
                  ? ""
                  : "not-allowed"
              }`}
            data-bs-toggle="modal"
            data-bs-target="#delete-modal"
            onClick={() => openDeleteModal(record._id, record.name)}
            disabled={isRoleProtected(record.name)}
            title={
              isRoleProtected(record.name)
                ? "This role is protected and cannot be deleted"
                : "Delete role"
            }
          >
            <i
              className={`ti ti-trash-x ${isRoleProtected(record.name) ? "text-muted" : "text-danger"
                }`}
            />
          </button>
        </div>
      ),
    },
  ]

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Roles &amp; Permissions</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">User Management</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Roles &amp; Permissions
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                <button
                  className={`btn btn-primary d-flex align-items-center ${user.permissions?.includes("role.create") ||
                      !user.permissions?.length
                      ? ""
                      : "not-allowed"
                    }`}
                  onClick={e => {
                    if (
                      user.permissions?.includes("role.create") &&
                      !user.permissions?.length
                    ) {
                      e.preventDefault()
                      return
                    }
                    handleAddRoleClick()
                  }}
                >
                  <i className="ti ti-square-rounded-plus me-2" />
                  Add Role
                </button>
              </div>
            </div>
          </div>
          {/* /Page Header */}

          {/* Filter Section */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Roles &amp; Permissions List</h4>
            </div>
            <div className="card-body p-0 py-3">
              {/* Role Permission List */}
              <Table
                columns={columns}
                dataSource={roles}
                loading={loading}
                pagination={{ ...pagination, onChange: handlePageChange }}
              />
              {/* /Role Permission List */}
            </div>
          </div>
          {/* /Filter Section */}
        </div>
      </div>

      {/* Delete Modal */}
      <div className="modal fade" id="delete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center">
              <span className="delete-icon">
                <i className="ti ti-trash-x" />
              </span>
              <h4>Confirm Deletion</h4>
              <p>
                Are you sure you want to delete{" "}
                {deleteRoleData?.name
                  ? `"${deleteRoleData.name}"`
                  : "this role"}
                ? This action cannot be undone.
              </p>
              <div className="d-flex justify-content-center">
                <button
                  className="btn btn-light me-3"
                  data-bs-dismiss="modal"
                  onClick={() => setDeleteRoleData(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    if (deleteRoleData) {
                      handleDeleteRole(deleteRoleData.id)
                    }
                  }}
                  disabled={
                    loading ||
                    (deleteRoleData
                      ? isRoleProtected(deleteRoleData.name)
                      : false)
                  }
                >
                  {loading ? "Deleting..." : "Yes, Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Delete Modal */}
    </div>
  )
}

export default RolesPermissions
